// Cloudflare API Client for FEK

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

class CloudflareAPI {
  constructor() {
    this.baseUrl = API_URL;
    this.userId = this.getOrCreateUserId();
    this.websockets = new Map();
  }

  getOrCreateUserId() {
    let userId = localStorage.getItem('fek_user_id');
    if (!userId) {
      userId = 'user-' + crypto.randomUUID();
      localStorage.setItem('fek_user_id', userId);
    }
    return userId;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}/api${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    return response.json();
  }

  // Server methods
  async getServers() {
    return this.request('/servers');
  }

  async createServer(name, description = '') {
    return this.request('/servers', {
      method: 'POST',
      body: JSON.stringify({
        name,
        description,
        owner_id: this.userId,
      }),
    });
  }

  async joinServer(inviteCode) {
    return this.request(`/servers/join/${inviteCode}`, {
      method: 'POST',
      body: JSON.stringify({ user_id: this.userId }),
    });
  }

  // Channel methods
  async getChannels(serverId) {
    return this.request(`/channels/${serverId}`);
  }

  async createChannel(serverId, name, type = 'text') {
    return this.request(`/channels/${serverId}`, {
      method: 'POST',
      body: JSON.stringify({ name, type }),
    });
  }

  // Message methods
  async getMessages(serverId, channelId) {
    return this.request(`/messages/${serverId}/${channelId}`);
  }

  async sendMessage(serverId, channelId, content, username, avatar) {
    return this.request(`/messages/${serverId}/${channelId}`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: this.userId,
        username,
        content,
        avatar,
      }),
    });
  }

  // WebSocket for real-time chat
  connectToChannel(channelId, username, onMessage, onUserList, onTyping) {
    const wsUrl = this.baseUrl.replace('http', 'ws') + `/ws/${channelId}`;
    
    if (this.websockets.has(channelId)) {
      this.disconnectFromChannel(channelId);
    }

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected to channel:', channelId);
      ws.send(JSON.stringify({
        type: 'join',
        user: {
          id: this.userId,
          username,
        },
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'message':
            onMessage?.(data);
            break;
          case 'user_list':
            onUserList?.(data.users);
            break;
          case 'typing':
            onTyping?.(data);
            break;
          case 'connected':
            console.log('Session ID:', data.session_id);
            break;
          default:
            console.log('Unknown message type:', data.type);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected from channel:', channelId);
      this.websockets.delete(channelId);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.websockets.set(channelId, ws);
    return ws;
  }

  disconnectFromChannel(channelId) {
    const ws = this.websockets.get(channelId);
    if (ws) {
      ws.close();
      this.websockets.delete(channelId);
    }
  }

  sendTypingStatus(channelId, isTyping) {
    const ws = this.websockets.get(channelId);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping,
      }));
    }
  }

  sendVoiceStatus(channelId, status) {
    const ws = this.websockets.get(channelId);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'voice_status',
        status,
      }));
    }
  }

  disconnectAll() {
    for (const [channelId] of this.websockets) {
      this.disconnectFromChannel(channelId);
    }
  }
}

export const cloudflareAPI = new CloudflareAPI();
export default cloudflareAPI;
